import { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Star,
  Edit3,
  Image as ImageIcon,
  Video,
  Package,
  ShoppingBag,
  Ruler,
  Palette,
  Weight,
} from "lucide-react";
import { formatPrice } from "../../utils/productUtils";
import AdminProductService from "../../services/admin/AdminProductService";

const VideoProcessingIndicator = ({ productId, onVideoReady }) => {
  const [secondsLeft, setSecondsLeft] = useState(45);
  const [isChecking, setIsChecking] = useState(false);
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const checkStatus = async () => {
    try {
      const res = await AdminProductService.get(productId);
      const updated = res.data;
      if (updated?.video_status !== "processing") {
        onVideoReady(updated);
        return true;
      }
    } catch (e) {
      console.error("Failed to check status", e);
    }
    return false;
  };

  useEffect(() => {
    pollIntervalRef.current = setInterval(async () => {
      const isReady = await checkStatus();
      if (isReady) {
        clearInterval(pollIntervalRef.current);
        clearInterval(countdownIntervalRef.current);
      }
    }, 5000);

    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          clearInterval(pollIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [productId]);

  const handleManualCheck = async () => {
    setIsChecking(true);
    const isReady = await checkStatus();
    if (isReady) {
      clearInterval(pollIntervalRef.current);
      clearInterval(countdownIntervalRef.current);
    }
    setIsChecking(false);
  };

  const progress = ((45 - secondsLeft) / 45) * 100;

  return (
    <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-4 text-center rounded-[2rem] z-20">
      <div className="w-16 h-16 mb-4 relative">
        <svg className="transform -rotate-90 w-16 h-16">
          <circle cx="32" cy="32" r="28" stroke="rgba(139, 92, 246, 0.2)" strokeWidth="6" fill="none" />
          <circle
            cx="32"
            cy="32"
            r="28"
            stroke="#62397f"
            strokeWidth="6"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 28}`}
            strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Video size={24} className="text-[#62397f]" />
        </div>
      </div>
      <p className="text-white text-sm font-semibold mb-1">Processing Video</p>
      <p className="text-white/60 text-xs mb-3">{secondsLeft}s remaining</p>
      <button
        onClick={handleManualCheck}
        disabled={isChecking}
        className="px-4 py-2 bg-[#62397f] hover:bg-[#7C3AED] disabled:bg-[#62397f]/50 text-white text-xs rounded-lg transition-all active:scale-95"
      >
        {isChecking ? "Checking..." : "Check Status"}
      </button>
    </div>
  );
};

export function ViewProductModal({ product: initialProduct, onClose, onEditProduct }) {
  const [product, setProduct] = useState(initialProduct);

  useEffect(() => {
    setProduct(initialProduct);
  }, [initialProduct]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!product) return null;

  const images = product.images || [product.image];
  const imageCount = images.length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 md:p-8 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#1E1E1E] rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#1E1E1E] z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#62397f]/10 flex items-center justify-center text-[#62397f]">
              <Package size={24} />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                {product.name}
              </h2>
              <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-inter uppercase tracking-widest font-bold">
                {product.category}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEditProduct(product)}
              className="flex items-center gap-2 px-4 py-2 bg-[#62397f] text-white rounded-xl font-bold text-sm font-inter hover:bg-[#7C3AED] transition-all hover:shadow-lg active:scale-95"
            >
              <Edit3 size={16} />
              <span className="hidden sm:inline">Edit Product</span>
            </button>
            <button
              onClick={onClose}
              className="p-3 rounded-xl hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all active:scale-90"
            >
              <X size={24} className="text-[#666666] dark:text-[#A0A0A0]" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-10 custom-scrollbar">
          {/* Top Section: Media & Quick Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image/Video Carousel */}
            <div className="space-y-4">
              <div className="relative aspect-square md:aspect-[4/3] rounded-[2rem] overflow-hidden border border-[#E5E7EB] dark:border-[#2A2A2A] bg-[#F9FAFB] dark:bg-[#1A1A1A] group">
                <img
                  src={images[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                {imageCount > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setCurrentImageIndex(
                          (currentImageIndex - 1 + imageCount) % imageCount,
                        )
                      }
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 dark:bg-black/80 rounded-2xl shadow-xl hover:bg-white transition-all active:scale-90"
                    >
                      <ChevronLeft
                        size={24}
                        className="text-[#111111] dark:text-white"
                      />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentImageIndex(
                          (currentImageIndex + 1) % imageCount,
                        )
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 dark:bg-black/80 rounded-2xl shadow-xl hover:bg-white transition-all active:scale-90"
                    >
                      <ChevronRight
                        size={24}
                        className="text-[#111111] dark:text-white"
                      />
                    </button>
                  </>
                )}

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentImageIndex
                        ? "bg-[#62397f] w-8"
                        : "bg-white/50 w-2 hover:bg-white/80"
                        }`}
                    />
                  ))}
                </div>

                <div className="absolute top-4 left-4 flex gap-2">
                  <div className="px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg text-white text-[10px] font-bold flex items-center gap-1.5">
                    <ImageIcon size={12} />
                    {imageCount} {imageCount === 1 ? "PHOTO" : "PHOTOS"}
                  </div>
                  {(product.video || product.video_status === "processing") && (
                    <div className="px-3 py-1.5 bg-[#62397f]/80 backdrop-blur-md rounded-lg text-white text-[10px] font-bold flex items-center gap-1.5">
                      <Video size={12} />
                      VIDEO ATTACHED
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnails */}
              {imageCount > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${idx === currentImageIndex
                        ? "border-[#62397f] scale-105"
                        : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                    >
                      <img
                        src={img}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Stats & Description */}
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-[#F9FAFB] dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] transition-all hover:shadow-md">
                  <div className="flex items-center gap-2 mb-2 text-[#666666] dark:text-[#A0A0A0]">
                    <ShoppingBag size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider font-inter">
                      Rental Price
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-[#62397f] font-poppins">
                    ₦{formatPrice(product.price)}
                  </p>
                </div>
                <div className="p-5 bg-[#F9FAFB] dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] transition-all hover:shadow-md">
                  <div className="flex items-center gap-2 mb-2 text-[#666666] dark:text-[#A0A0A0]">
                    <Star size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider font-inter">
                      Status
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-bold ${product.status === "active"
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-100 text-gray-600"
                        }`}
                    >
                      {product.status.toUpperCase()}
                    </span>
                    {product.popular && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-[10px] font-bold flex items-center gap-1">
                        <Star size={10} fill="currentColor" />
                        POPULAR
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-[#111111] dark:text-white font-poppins uppercase tracking-wider mb-3">
                  Description
                </h3>
                <p className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter leading-relaxed bg-[#FAFAFA] dark:bg-[#1A1A1A] p-4 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
                  {product.description ||
                    "No description provided for this product."}
                </p>
              </div>

              <div className="p-5 bg-gradient-to-br from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#1A1A1A] rounded-2xl border border-[#62397f]/20">
                <p className="text-xs font-bold text-[#62397f] uppercase tracking-widest mb-2">
                  Inventory Summary
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#111111] dark:text-white font-poppins">
                    {product.quantity}
                  </span>
                  <span className="text-sm font-medium text-[#666666] dark:text-[#A0A0A0] font-inter">
                    units available for rent
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications Section */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                Specifications
              </h3>
              <div className="h-[1px] flex-1 bg-[#E5E7EB] dark:bg-[#2A2A2A]"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: "Weight",
                  value: product.specifications?.weight,
                  icon: Weight,
                },
                {
                  label: "Color",
                  value: product.specifications?.colour,
                  icon: Palette,
                },
                {
                  label: "Material",
                  value: product.specifications?.material,
                  icon: Package,
                },
                {
                  label: "Dimensions",
                  value: product.specifications?.dimensions,
                  icon: Ruler,
                },
              ].map((spec, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 p-4 bg-white dark:bg-[#1E1E1E] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] transition-all hover:border-[#62397f]/40 hover:shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F9FAFB] dark:bg-[#262626] flex items-center justify-center text-[#62397f]">
                    <spec.icon size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">
                      {spec.label}
                    </p>
                    <p className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
                      {spec.value || "N/A"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Video Player Section */}
          {(product.video || product.video_status === "processing") && (
            <div>
              <div className="flex items-center gap-4 mb-6">
                <h3 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                  Product Video
                </h3>
                <div className="h-[1px] flex-1 bg-[#E5E7EB] dark:bg-[#2A2A2A]"></div>
              </div>
              <div className="relative aspect-video rounded-[2rem] overflow-hidden border border-[#E5E7EB] dark:border-[#2A2A2A] bg-black group">
                {product.video_status === "processing" ? (
                  <VideoProcessingIndicator
                    productId={product.id}
                    onVideoReady={(updated) => setProduct(updated)}
                  />
                ) : (
                  <video
                    controls
                    className="w-full h-full object-contain"
                    src={product.video}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E5E7EB;
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #2A2A2A;
        }
      `}</style>
    </div>
  );
}
